/**
 * @file tagsView.js
 * @description 已访问页签与 keep-alive 缓存名管理
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * @typedef {Object} VisitedViewType
 * @property {string} name
 * @property {string} path
 * @property {string} fullPath
 * @property {string} title
 * @property {boolean} affix
 */

export const useTagsViewStore = defineStore('tagsView', () => {
  const visitedViews = ref([])
  const cachedViews = ref([])

  /**
   * 记录访问页签（按 path 去重：同 path 仅更新 fullPath，避免分页/query 重复开签）
   * @param {import('vue-router').RouteLocationNormalizedLoaded} route
   */
  function addView(route) {
    if (!route.name || route.meta?.noTagsView) return

    const title = route.meta?.title || String(route.name)
    const existingIndex = visitedViews.value.findIndex((item) => item.path === route.path)
    if (existingIndex > -1) {
      const existing = visitedViews.value[existingIndex]
      visitedViews.value[existingIndex] = {
        ...existing,
        name: String(route.name),
        fullPath: route.fullPath,
        title,
      }
      addCachedView(route)
      return
    }

    visitedViews.value.push({
      name: String(route.name),
      path: route.path,
      fullPath: route.fullPath,
      title,
      affix: !!route.meta?.affix,
    })
    addCachedView(route)
  }

  /**
   * 加入 keep-alive 缓存列表
   * @param {import('vue-router').RouteLocationNormalizedLoaded|Object} route
   */
  function addCachedView(route) {
    if (!route.name || route.meta?.noCache) return
    const name = String(route.name)
    if (!cachedViews.value.includes(name)) {
      cachedViews.value.push(name)
    }
  }

  /**
   * 移除页签
   * @param {VisitedViewType} view
   * @returns {VisitedViewType[]}
   */
  function delView(view) {
    if (view.affix) return visitedViews.value

    const index = visitedViews.value.findIndex((item) => item.path === view.path)
    if (index === -1) return visitedViews.value

    visitedViews.value.splice(index, 1)
    delCachedView(view)
    return visitedViews.value
  }

  /**
   * 移除组件缓存（关闭页签时；同名页签仍存在则保留缓存）
   * @param {VisitedViewType} view
   */
  function delCachedView(view) {
    if (!view.name) return
    const hasOther = visitedViews.value.some((item) => item.name === view.name)
    if (!hasOther) {
      const index = cachedViews.value.indexOf(view.name)
      if (index > -1) {
        cachedViews.value.splice(index, 1)
      }
    }
  }

  /**
   * 刷新页签：强制移除 keep-alive 缓存
   * @param {VisitedViewType} view
   */
  function refreshCachedView(view) {
    if (!view.name) return
    const index = cachedViews.value.indexOf(view.name)
    if (index > -1) {
      cachedViews.value.splice(index, 1)
    }
  }

  /**
   * 关闭其他页签
   * @param {VisitedViewType} view
   */
  function delOthersViews(view) {
    visitedViews.value = visitedViews.value.filter(
      (item) => item.affix || item.path === view.path,
    )
    rebuildCachedViews()
  }

  /**
   * 关闭全部可关闭页签
   */
  function delAllViews() {
    visitedViews.value = visitedViews.value.filter((item) => item.affix)
    rebuildCachedViews()
  }

  /**
   * 根据当前页签重建缓存列表
   */
  function rebuildCachedViews() {
    cachedViews.value = [
      ...new Set(
        visitedViews.value
          .map((item) => item.name)
          .filter(Boolean),
      ),
    ]
  }

  /**
   * 初始化固定页签（如首页）
   * @param {import('vue-router').RouteRecordNormalized[]} routes
   */
  function initAffixTags(routes) {
    routes.forEach((route) => {
      if (!route.name || !route.meta?.affix) return
      addView({
        name: route.name,
        path: route.path,
        fullPath: route.path,
        meta: route.meta,
      })
    })
  }

  /**
   * 退出登录时清空
   */
  function reset() {
    visitedViews.value = []
    cachedViews.value = []
  }

  return {
    visitedViews,
    cachedViews,
    addView,
    addCachedView,
    delView,
    delCachedView,
    refreshCachedView,
    delOthersViews,
    delAllViews,
    initAffixTags,
    reset,
  }
})
